import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Autocomplete,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Inventory as InventoryIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Save as SaveIcon,
  ShoppingCart as PurchaseIcon,
  Business as VendorIcon,
  Receipt as InvoiceIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";

const StockInPurchaseEntry = () => {
  // Sample vendors data
  const vendors = [
    { id: 1, code: "VEN001", name: "ABC Suppliers Pvt Ltd", gst: "27AABCU9603R1ZM" },
    { id: 2, code: "VEN002", name: "XYZ Wholesale", gst: "07AABCU9603R1ZM" },
    { id: 3, code: "VEN003", name: "Global Imports LLC", gst: "29AABCU9603R1ZM" },
    { id: 4, code: "VEN004", name: "Local Traders", gst: "24AABCU9603R1ZM" },
    { id: 5, code: "VEN005", name: "Premium Products Inc", gst: "27AABCU9603R1ZN" },
  ];

  // Sample products data
  const products = [
    { id: 1, code: "PRD001", name: "Samsung Galaxy S23", category: "Electronics", unit: "Piece", tax: 18 },
    { id: 2, code: "PRD002", name: "Apple iPhone 14", category: "Electronics", unit: "Piece", tax: 18 },
    { id: 3, code: "PRD003", name: "Sony LED TV 55\"", category: "Electronics", unit: "Piece", tax: 18 },
    { id: 4, code: "PRD004", name: "HP Laptop i5", category: "Electronics", unit: "Piece", tax: 18 },
    { id: 5, code: "PRD005", name: "Nike Sports Shoes", category: "Clothing", unit: "Pair", tax: 12 },
    { id: 6, code: "PRD006", name: "Adidas T-Shirt", category: "Clothing", unit: "Piece", tax: 12 },
    { id: 7, code: "PRD007", name: "Levi's Jeans", category: "Clothing", unit: "Piece", tax: 12 },
    { id: 8, code: "PRD008", name: "LG Refrigerator 190L", category: "Home Appliances", unit: "Piece", tax: 18 },
    { id: 9, code: "PRD009", name: "Microwave Oven", category: "Home Appliances", unit: "Piece", tax: 18 },
    { id: 10, code: "PRD010", name: "Rice 1kg", category: "Groceries", unit: "Kg", tax: 5 },
  ];

  // Purchase entries data
  const [purchaseData, setPurchaseData] = useState([
    {
      id: 1,
      purchaseNo: "PO-2024-001",
      purchaseDate: "2024-01-15",
      vendorName: "ABC Suppliers Pvt Ltd",
      invoiceNo: "INV-001",
      invoiceDate: "2024-01-15",
      totalItems: 3,
      totalQuantity: 25,
      subtotal: 125000,
      taxAmount: 22500,
      discount: 5000,
      grandTotal: 142500,
      paymentStatus: "Paid",
      status: "Completed",
      createdDate: "2024-01-15",
    },
    {
      id: 2,
      purchaseNo: "PO-2024-002",
      purchaseDate: "2024-01-20",
      vendorName: "XYZ Wholesale",
      invoiceNo: "INV-002",
      invoiceDate: "2024-01-20",
      totalItems: 5,
      totalQuantity: 50,
      subtotal: 89000,
      taxAmount: 16020,
      discount: 3000,
      grandTotal: 102020,
      paymentStatus: "Pending",
      status: "Completed",
      createdDate: "2024-01-20",
    },
    {
      id: 3,
      purchaseNo: "PO-2024-003",
      purchaseDate: "2024-01-25",
      vendorName: "Global Imports LLC",
      invoiceNo: "INV-003",
      invoiceDate: "2024-01-25",
      totalItems: 2,
      totalQuantity: 10,
      subtotal: 156000,
      taxAmount: 28080,
      discount: 10000,
      grandTotal: 174080,
      paymentStatus: "Partial",
      status: "Completed",
      createdDate: "2024-01-25",
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [viewingPurchase, setViewingPurchase] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Purchase form state
  const [purchaseForm, setPurchaseForm] = useState({
    purchaseNo: "",
    purchaseDate: new Date().toISOString().split('T')[0],
    vendorId: null,
    invoiceNo: "",
    invoiceDate: new Date().toISOString().split('T')[0],
    paymentTerms: "Net 30",
    shippingCharges: 0,
    otherCharges: 0,
    discountType: "Fixed",
    discountValue: 0,
    notes: "",
    status: "Draft",
  });

  // Purchase items state
  const [purchaseItems, setPurchaseItems] = useState([
    {
      id: Date.now(),
      productId: null,
      productName: "",
      quantity: 1,
      rate: 0,
      taxRate: 0,
      discount: 0,
      amount: 0,
    }
  ]);

  // Helper functions
  const getFilteredPurchases = () => {
    return purchaseData.filter((purchase) => {
      const matchesSearch = Object.values(purchase).some((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchesStatus = statusFilter === "all" || purchase.status === statusFilter;
      const matchesPayment = paymentFilter === "all" || purchase.paymentStatus === paymentFilter;
      return matchesSearch && matchesStatus && matchesPayment;
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Completed':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Draft':
        return { backgroundColor: '#fff3e0', color: '#e65100' };
      case 'Cancelled':
        return { backgroundColor: '#ffebee', color: '#d32f2f' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  const getPaymentStatusStyle = (status) => {
    switch (status) {
      case 'Paid':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Pending':
        return { backgroundColor: '#ffebee', color: '#d32f2f' };
      case 'Partial':
        return { backgroundColor: '#fff3e0', color: '#e65100' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate item amount
  const calculateItemAmount = (item) => {
    const baseAmount = item.quantity * item.rate;
    const discountAmount = item.discount;
    const taxableAmount = baseAmount - discountAmount;
    const taxAmount = (taxableAmount * item.taxRate) / 100;
    return taxableAmount + taxAmount;
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    purchaseItems.forEach(item => {
      if (item.productId) {
        const baseAmount = item.quantity * item.rate;
        subtotal += baseAmount;
        totalDiscount += item.discount;
        const taxableAmount = baseAmount - item.discount;
        totalTax += (taxableAmount * item.taxRate) / 100;
      }
    });

    const itemsTotal = subtotal - totalDiscount + totalTax;
    
    // Additional discount
    let additionalDiscount = 0;
    if (purchaseForm.discountType === "Fixed") {
      additionalDiscount = purchaseForm.discountValue;
    } else {
      additionalDiscount = (itemsTotal * purchaseForm.discountValue) / 100;
    }

    const grandTotal = itemsTotal - additionalDiscount + 
                      parseFloat(purchaseForm.shippingCharges || 0) + 
                      parseFloat(purchaseForm.otherCharges || 0);

    return {
      subtotal,
      totalTax,
      totalDiscount,
      additionalDiscount,
      grandTotal: grandTotal > 0 ? grandTotal : 0,
    };
  };

  // Handlers
  const handleEdit = (purchase) => {
    // In a real app, you'd load the full purchase details including items
    setEditingPurchase(purchase);
    setOpenModal(true);
  };

  const handleView = (purchase) => {
    setViewingPurchase(purchase);
    setOpenViewModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this purchase entry?")) {
      setPurchaseData(purchaseData.filter((purchase) => purchase.id !== id));
      setSuccessMessage("Purchase entry deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    setSuccessMessage(`Search completed. Found ${getFilteredPurchases().length} entries.`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReset = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setPaymentFilter("all");
    setPage(0);
    setErrors({});
    setSuccessMessage("");
  };

  const handleOpenModal = () => {
    setEditingPurchase(null);
    const newPurchaseNo = `PO-${new Date().getFullYear()}-${String(purchaseData.length + 1).padStart(3, '0')}`;
    setPurchaseForm({
      purchaseNo: newPurchaseNo,
      purchaseDate: new Date().toISOString().split('T')[0],
      vendorId: null,
      invoiceNo: "",
      invoiceDate: new Date().toISOString().split('T')[0],
      paymentTerms: "Net 30",
      shippingCharges: 0,
      otherCharges: 0,
      discountType: "Fixed",
      discountValue: 0,
      notes: "",
      status: "Draft",
    });
    setPurchaseItems([
      {
        id: Date.now(),
        productId: null,
        productName: "",
        quantity: 1,
        rate: 0,
        taxRate: 0,
        discount: 0,
        amount: 0,
      }
    ]);
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingPurchase(null);
    setErrors({});
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setViewingPurchase(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setPurchaseForm({ ...purchaseForm, [name]: value });
  };

  const handleVendorChange = (event, newValue) => {
    setPurchaseForm({ 
      ...purchaseForm, 
      vendorId: newValue ? newValue.id : null 
    });
  };

  const handleProductChange = (index, event, newValue) => {
    const updatedItems = [...purchaseItems];
    if (newValue) {
      updatedItems[index] = {
        ...updatedItems[index],
        productId: newValue.id,
        productName: newValue.name,
        taxRate: newValue.tax,
      };
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        productId: null,
        productName: "",
        taxRate: 0,
      };
    }
    setPurchaseItems(updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...purchaseItems];
    updatedItems[index][field] = parseFloat(value) || 0;
    updatedItems[index].amount = calculateItemAmount(updatedItems[index]);
    setPurchaseItems(updatedItems);
  };

  const handleAddItem = () => {
    setPurchaseItems([
      ...purchaseItems,
      {
        id: Date.now(),
        productId: null,
        productName: "",
        quantity: 1,
        rate: 0,
        taxRate: 0,
        discount: 0,
        amount: 0,
      }
    ]);
  };

  const handleRemoveItem = (index) => {
    if (purchaseItems.length > 1) {
      const updatedItems = purchaseItems.filter((_, i) => i !== index);
      setPurchaseItems(updatedItems);
    }
  };

  const handleSavePurchase = async (isDraft = false) => {
    // Validation
    if (!purchaseForm.vendorId) {
      setErrors({ vendor: "Please select a vendor" });
      return;
    }
    if (!purchaseForm.invoiceNo) {
      setErrors({ invoice: "Please enter invoice number" });
      return;
    }
    if (purchaseItems.filter(item => item.productId).length === 0) {
      setErrors({ items: "Please add at least one product" });
      return;
    }

    setIsLoading(true);
    try {
      const totals = calculateTotals();
      const vendor = vendors.find(v => v.id === purchaseForm.vendorId);
      const validItems = purchaseItems.filter(item => item.productId);

      const newPurchase = {
        id: purchaseData.length + 1,
        purchaseNo: purchaseForm.purchaseNo,
        purchaseDate: purchaseForm.purchaseDate,
        vendorName: vendor.name,
        invoiceNo: purchaseForm.invoiceNo,
        invoiceDate: purchaseForm.invoiceDate,
        totalItems: validItems.length,
        totalQuantity: validItems.reduce((sum, item) => sum + item.quantity, 0),
        subtotal: totals.subtotal,
        taxAmount: totals.totalTax,
        discount: totals.totalDiscount + totals.additionalDiscount,
        grandTotal: totals.grandTotal,
        paymentStatus: "Pending",
        status: isDraft ? "Draft" : "Completed",
        createdDate: new Date().toISOString().split('T')[0],
      };

      setPurchaseData([newPurchase, ...purchaseData]);
      setSuccessMessage(`Purchase ${isDraft ? 'draft saved' : 'entry created'} successfully!`);
      handleCloseModal();
    } catch (error) {
      setErrors({ general: "An error occurred. Please try again." });
    } finally {
      setIsLoading(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const filteredPurchases = getFilteredPurchases();
  const totals = calculateTotals();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <InventoryIcon sx={{ color: 'primary.main', fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Stock In / Purchase Entry
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage incoming stock and purchase orders • {filteredPurchases.length} entries found
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          New Purchase Entry
        </Button>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Purchase Entries..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField
              select
              fullWidth
              label="Payment Status"
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Payments</option>
              <option value="Paid">Paid</option>
              <option value="Pending">Pending</option>
              <option value="Partial">Partial</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField
              select
              fullWidth
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Status</option>
              <option value="Completed">Completed</option>
              <option value="Draft">Draft</option>
              <option value="Cancelled">Cancelled</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ flex: 1 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                sx={{ flex: 1 }}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Purchase No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Vendor Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Invoice No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Items/Qty</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Grand Total</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Payment</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPurchases.length > 0 ? (
                filteredPurchases
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((purchase, index) => (
                    <TableRow 
                      key={purchase.id} 
                      sx={{ 
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600" sx={{ fontFamily: 'monospace' }}>
                          {purchase.purchaseNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {purchase.purchaseDate}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {purchase.vendorName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {purchase.invoiceNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {purchase.totalItems} items
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {purchase.totalQuantity} qty
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="700" color="primary.main">
                          {formatCurrency(purchase.grandTotal)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={purchase.paymentStatus}
                          size="small"
                          sx={{
                            ...getPaymentStatusStyle(purchase.paymentStatus),
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={purchase.status}
                          size="small"
                          sx={{
                            ...getStatusStyle(purchase.status),
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(purchase)}
                              sx={{
                                color: 'info.main',
                                '&:hover': { backgroundColor: 'info.light' },
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(purchase)}
                              sx={{
                                color: 'warning.main',
                                '&:hover': { backgroundColor: 'warning.light' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(purchase.id)}
                              sx={{
                                color: 'error.main',
                                '&:hover': { backgroundColor: 'error.light' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <PurchaseIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No purchase entries found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredPurchases.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Purchase Entry Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="xl" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <PurchaseIcon />
            {editingPurchase ? 'Edit Purchase Entry' : 'New Purchase Entry'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          {/* Purchase Header Information */}
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
              Purchase Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Purchase No"
                  name="purchaseNo"
                  value={purchaseForm.purchaseNo}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <InvoiceIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Purchase Date"
                  name="purchaseDate"
                  type="date"
                  value={purchaseForm.purchaseDate}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={vendors}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  onChange={handleVendorChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Vendor / Supplier *"
                      error={!!errors.vendor}
                      helperText={errors.vendor}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <VendorIcon />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Invoice No *"
                  name="invoiceNo"
                  value={purchaseForm.invoiceNo}
                  onChange={handleFormChange}
                  error={!!errors.invoice}
                  helperText={errors.invoice}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <InvoiceIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Invoice Date"
                  name="invoiceDate"
                  type="date"
                  value={purchaseForm.invoiceDate}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Payment Terms"
                  name="paymentTerms"
                  value={purchaseForm.paymentTerms}
                  onChange={handleFormChange}
                >
                  <MenuItem value="Cash on Delivery">Cash on Delivery</MenuItem>
                  <MenuItem value="Net 7">Net 7 Days</MenuItem>
                  <MenuItem value="Net 15">Net 15 Days</MenuItem>
                  <MenuItem value="Net 30">Net 30 Days</MenuItem>
                  <MenuItem value="Net 45">Net 45 Days</MenuItem>
                  <MenuItem value="Net 60">Net 60 Days</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>

          {/* Product Items Table */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" color="primary">
                Product Items
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                size="small"
              >
                Add Item
              </Button>
            </Box>
            {errors.items && (
              <Alert severity="error" sx={{ mb: 2 }}>{errors.items}</Alert>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>S.No</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Rate</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Tax %</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Discount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '6%' }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseItems.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Autocomplete
                          options={products}
                          getOptionLabel={(option) => `${option.code} - ${option.name}`}
                          onChange={(event, newValue) => handleProductChange(index, event, newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Product"
                              size="small"
                              fullWidth
                            />
                          )}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          inputProps={{ min: 1, step: 1 }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={item.rate}
                          onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                          inputProps={{ min: 0, step: 0.01 }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={item.taxRate}
                          onChange={(e) => handleItemChange(index, 'taxRate', e.target.value)}
                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={item.discount}
                          onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                          inputProps={{ min: 0, step: 0.01 }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(item.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveItem(index)}
                          disabled={purchaseItems.length === 1}
                          color="error"
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Additional Charges and Totals */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  Additional Charges
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Shipping Charges"
                      name="shippingCharges"
                      type="number"
                      value={purchaseForm.shippingCharges}
                      onChange={handleFormChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ShippingIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Other Charges"
                      name="otherCharges"
                      type="number"
                      value={purchaseForm.otherCharges}
                      onChange={handleFormChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MoneyIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      fullWidth
                      label="Discount Type"
                      name="discountType"
                      value={purchaseForm.discountType}
                      onChange={handleFormChange}
                    >
                      <MenuItem value="Fixed">Fixed Amount</MenuItem>
                      <MenuItem value="Percentage">Percentage</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label={purchaseForm.discountType === "Fixed" ? "Discount Amount" : "Discount %"}
                      name="discountValue"
                      type="number"
                      value={purchaseForm.discountValue}
                      onChange={handleFormChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      name="notes"
                      value={purchaseForm.notes}
                      onChange={handleFormChange}
                      multiline
                      rows={3}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, backgroundColor: '#f8f9fa', height: '100%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  Summary
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Subtotal:</Typography>
                    <Typography variant="body1" fontWeight="600">
                      {formatCurrency(totals.subtotal)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Item Discount:</Typography>
                    <Typography variant="body1" fontWeight="600" color="error.main">
                      - {formatCurrency(totals.totalDiscount)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Tax Amount:</Typography>
                    <Typography variant="body1" fontWeight="600">
                      {formatCurrency(totals.totalTax)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Additional Discount:</Typography>
                    <Typography variant="body1" fontWeight="600" color="error.main">
                      - {formatCurrency(totals.additionalDiscount)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Shipping Charges:</Typography>
                    <Typography variant="body1" fontWeight="600">
                      {formatCurrency(purchaseForm.shippingCharges)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Other Charges:</Typography>
                    <Typography variant="body1" fontWeight="600">
                      {formatCurrency(purchaseForm.otherCharges)}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="h6" fontWeight="bold">Grand Total:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      {formatCurrency(totals.grandTotal)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {errors.general && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.general}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button onClick={handleCloseModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleSavePurchase(true)}
            disabled={isLoading}
            startIcon={<SaveIcon />}
          >
            Save as Draft
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSavePurchase(false)}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            {isLoading ? 'Processing...' : 'Complete Purchase'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Purchase Modal */}
      <Dialog open={openViewModal} onClose={handleCloseViewModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon />
            Purchase Entry Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          {viewingPurchase && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Purchase No</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1, fontFamily: 'monospace' }}>
                    {viewingPurchase.purchaseNo}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Purchase Date</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingPurchase.purchaseDate}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Vendor Name</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingPurchase.vendorName}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Invoice No</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1, fontFamily: 'monospace' }}>
                    {viewingPurchase.invoiceNo}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Total Items / Quantity</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingPurchase.totalItems} items / {viewingPurchase.totalQuantity} qty
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {formatCurrency(viewingPurchase.subtotal)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Tax Amount</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {formatCurrency(viewingPurchase.taxAmount)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Discount</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }} color="error.main">
                    - {formatCurrency(viewingPurchase.discount)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(144, 19, 254, 0.1)' }}>
                  <Typography variant="caption" color="text.secondary">Grand Total</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }} color="primary.main">
                    {formatCurrency(viewingPurchase.grandTotal)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(144, 19, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Payment Status</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={viewingPurchase.paymentStatus}
                      sx={{
                        ...getPaymentStatusStyle(viewingPurchase.paymentStatus),
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseViewModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockInPurchaseEntry;

